import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { routeBase, studyName, studyThumbnail, useCollection } from '../api/collections';
import type { ApiError } from '../../features/collections/api';
import { CannotUploadHere, CollectionGone } from '../ui/Gone';
import { Cover } from '../ui/Cards';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Controls';
import { SearchField } from '../ui/Field';
import { IconCheck, IconChevronRight } from '../ui/icons';
import { StudyFields, studyHints } from './DetailsFields';
import { generateStudyId, sessionById, sessionForStudy, sessionRoute, stepOneRoute, uploadWork, useUpload, type FieldError, type UploadSession } from './store';

/**
 * `study`: an upload started from a study's page names that study between
 * the location and "Upload", and links back to it, so the page reads as
 * adding to THAT study rather than as a collection upload.
 */
export function Breadcrumb({ wsName, locationName, onBack, last, study }: { wsName: string; locationName: string; onBack: () => void; last: string; study?: { name: string; onOpen: () => void } }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#6b6b70' }}>
      <span>{wsName}</span>
      <IconChevronRight size={12} />
      <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }} style={{ color: '#6b6b70', textDecoration: 'none' }}>{locationName}</a>
      <IconChevronRight size={12} />
      {study ? (
        <>
          <a href="#" onClick={(e) => { e.preventDefault(); study.onOpen(); }} style={{ color: '#6b6b70', textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace" }}>{study.name}</a>
          <IconChevronRight size={12} />
        </>
      ) : null}
      <span style={{ color: '#0f0f10' }}>{last}</span>
    </div>
  );
}

export function StepIndicator({ step }: { step: 1 | 2 }) {
  const dot = (n: 1 | 2, label: string) => {
    const done = step > n;
    const on = step === n;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: on || done ? '#0f0f10' : 'transparent', border: on || done ? undefined : '1.5px solid #d4d4d2', color: on || done ? '#fff' : '#8a8a8f', display: 'grid', placeItems: 'center', fontSize: 11.5, fontWeight: 600 }}>
          {done ? <IconCheck size={11} sw={3} color="#fff" /> : n}
        </div>
        <span style={{ fontSize: 13.5, fontWeight: 500, color: on || done ? '#0f0f10' : '#8a8a8f' }}>{label}</span>
      </div>
    );
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 28px 0', gap: 8 }}>
      {dot(1, 'Study')}
      <div style={{ width: 40, height: 1, background: '#e3e3e1', margin: '0 8px' }} />
      {dot(2, 'Upload')}
    </div>
  );
}

/**
 * Uploader step 1, "Study" (`is.uploadStudy` in the prototype): mode switch,
 * PHI notice, the New study form or the Existing study picker, the location
 * card, Continue. Column 800px on #fafaf9.
 *
 * Continue makes the new study on the server (a draft, filed into the
 * collection and group), so step 2 always adds images to a study that exists;
 * coming Back and continuing again saves the edits to that same study.
 */
export function UploadStudyPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { current } = useSelectedWorkspace();
  const { data: col, error: colError } = useCollection(publicId);
  // The session this page edits is named in the address (`upload`): Back
  // from its files page comes to it, and a reload keeps it. None named is a
  // new upload.
  const uploadId = params.get('upload');
  const found = useUpload((st) => sessionById(st.sessions, uploadId));
  const session = found && found.colId === publicId ? found : null;
  const patch = (p: Partial<UploadSession>) => {
    if (session) useUpload.getState().patch(session.id, p);
  };
  const cancel = () => {
    if (session) useUpload.getState().cancel(session.id);
  };
  const grpId = params.get('group');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<FieldError | null>(null);

  // Only once the collection says this person may write to it: a session
  // started earlier was one for a page that then said "you can't upload here".
  // Starting one never touches another in flight (store.ts start).
  const mayUpload = col?.editable === true;
  useEffect(() => {
    if (!publicId || !mayUpload) return;
    const open = sessionById(useUpload.getState().sessions, uploadId);
    if (open && open.colId === publicId) return;
    const id = useUpload.getState().start({ colId: publicId, grpId: grpId ?? null, locationName });
    nav(stepOneRoute(routeBase(), { id, colId: publicId, grpId: grpId ?? null }), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, grpId, uploadId, mayUpload]);

  const colGone = (colError as ApiError | null)?.status === 404;
  // The destination is gone, so the session has nowhere to be filed: let it
  // go (UploadFilesPage carries the same rule and the longer note).
  const sessionId = session?.id;
  useEffect(() => {
    if (colGone && sessionId) useUpload.getState().cancel(sessionId);
  }, [colGone, sessionId]);

  const group = col?.groups.find((g) => g.public_id === grpId) ?? null;
  const locationName = group ? group.name : col?.name ?? '';
  const candidates = useMemo(() => {
    if (!col || !session) return [];
    const q = session.search.trim().toLowerCase();
    return col.studies.filter((s) => (group ? s.group_public_id === group.public_id : true) && (!q || studyName(s).toLowerCase().includes(q)));
  }, [col, session, group]);
  const back = () => nav(`${routeBase()}/collections/${publicId}`);

  if (colGone) return <CollectionGone body="It was deleted while this page was open, so there is nowhere to file these images." />;
  // Before the form, not after the bytes: this page is reachable by address.
  if (col && col.editable !== true) return <CannotUploadHere collectionId={publicId} />;
  if (!col || !session) return <div style={{ flex: 1, background: '#fafaf9' }} />;
  const isNew = session.mode === 'new';
  // The client's early word on identifier-like text; the server's answer on Continue is the one that counts.
  const hints = studyHints(session);
  const canContinue = !saving && (isNew ? !!session.label.trim() && !hints.diagnosis && !hints.description : !!session.studyId);
  const target = !isNew && session.studyId ? col.studies.find((s) => s.public_id === session.studyId) : null;
  // Images already added belong to the study they were made in: the choice is made.
  const locked = session.files.length > 0;
  const said = (field: string, hint: string | null): ReactNode => {
    const text = refusal?.field === field ? refusal.message : hint ? `Remove ${hint}.` : null;
    return text ? <span style={{ color: '#d9342b' }}>{text}</span> : undefined;
  };
  const edit = (p: Partial<UploadSession>) => {
    setRefusal(null);
    patch(p);
  };
  const next = async () => {
    const store = useUpload.getState();
    // A study this tab is already sending images into: that upload carries
    // on, with these added there (one session per study). One whose images
    // are all settled is simply let go.
    if (session.mode === 'existing' && session.studyId) {
      const other = sessionForStudy(store.sessions, session.studyId);
      if (other && other.id !== session.id) {
        if (uploadWork(other) !== 'complete') {
          store.cancel(session.id);
          nav(sessionRoute(routeBase(), other));
          return;
        }
        store.dismiss(session.studyId);
      }
    }
    setSaving(true);
    const refused = await store.saveStudy(session.id);
    setSaving(false);
    setRefusal(refused);
    const now = sessionById(useUpload.getState().sessions, session.id);
    if (!refused && now) nav(sessionRoute(routeBase(), now));
  };

  const modeCard = (on: boolean, title: string, sub: string, onClick: () => void) => (
    <div
      className={on || locked ? undefined : 'tr-hover-track'}
      onClick={locked ? undefined : onClick}
      title={locked && !on ? 'The images already added belong to this study. Remove them to choose another.' : undefined}
      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 10, cursor: locked ? 'default' : 'pointer', opacity: locked && !on ? 0.5 : 1, background: on ? '#fff' : 'transparent', color: '#0f0f10', boxShadow: on ? '0 1px 3px rgba(0,0,0,.10), 0 0 0 1px #e3e3e1' : 'none' }}
    >
      <div style={{ width: 20, height: 20, borderRadius: '50%', border: `1.5px solid ${on ? '#0f0f10' : '#d4d4d2'}`, background: on ? '#0f0f10' : 'transparent', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        {on ? <IconCheck size={11} sw={3} color="#fff" /> : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 12.5, color: '#6b6b70' }}>{sub}</span>
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fafaf9' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px 0' }}>
        <Breadcrumb wsName={current?.name ?? ''} locationName={locationName} onBack={back} last="Upload" />
        <Button variant="outlined" size={32} onClick={() => { cancel(); back(); }}>Cancel</Button>
      </div>
      <StepIndicator step={1} />
      <div className="tr-page" style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '24px 28px 40px' }}>
        <div style={{ width: '100%', maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 4, border: '1px solid #ebebea', borderRadius: 14, background: '#f6f6f4' }}>
              {modeCard(isNew, 'Create a new study', `Start a study in ${locationName} and add the images to it`, () => edit({ mode: 'new' }))}
              {modeCard(!isNew, 'Add to an existing study', `Pick one of the ${candidates.length} studies already in ${locationName}`, () => edit({ mode: 'existing' }))}
            </div>
          </div>
          <Notice>Do not enter any PHI, such as patient names, dates of birth, MRNs or accession numbers.</Notice>
          <div style={{ background: '#fff', border: '1px solid #ebebea', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '18px 24px 0' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{isNew ? 'New study' : 'Existing study'}</h2>
              <span style={{ fontSize: 13, color: '#8a8a8f' }}>{isNew ? '' : 'the images you upload will be added to it'}</span>
            </div>
            <div style={{ padding: '14px 24px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {isNew ? (
                <StudyFields
                  values={session}
                  onChange={edit}
                  onAgeBlur={(age) => patch({ age })}
                  onGenerate={() => edit({ label: generateStudyId(col.name, col.studies.map(studyName)) })}
                  said={said}
                />
              ) : (
                <>
                  <SearchField value={session.search} onChange={(search) => patch({ search })} placeholder={`Search the ${candidates.length} studies in ${locationName}`} h={36} fontSize={13.5} radius={7} />
                  <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 20 }}>
                    {candidates.map((s) => {
                      const on = s.public_id === session.studyId;
                      return (
                        // By public id: the images are made under it.
                        <div key={s.public_id} onClick={() => { if (!locked) edit({ studyId: s.public_id }); }} style={{ display: 'flex', flexDirection: 'column', gap: 10, cursor: locked ? 'default' : 'pointer' }}>
                          <div style={{ position: 'relative', aspectRatio: '4 / 3', marginTop: 12 }}>
                            <div style={{ position: 'absolute', left: 14, right: 14, top: -12, height: '70%', borderRadius: 7, background: '#ececea', border: '1px solid #ebebea' }} />
                            <div style={{ position: 'absolute', left: 7, right: 7, top: -6, height: '80%', borderRadius: 7, background: '#dcdcd9', border: '1px solid #ebebea' }} />
                            <Cover src={studyThumbnail(s.public_id)} fallback="#e8e8e6" radius={10} className="tr-hover-ring" style={{ position: 'absolute', inset: 0, border: '1px solid #ebebea', boxShadow: on ? '0 0 0 2px #fff, 0 0 0 4px #0f0f10' : 'none' }} />
                            {on ? (
                              <div style={{ position: 'absolute', top: 8, right: 8, height: 22, padding: '0 8px 0 6px', borderRadius: 5, background: '#0f0f10', color: '#fff', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500 }}>
                                <IconCheck size={11} sw={3} color="#fff" />
                                Selected
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 500 }}>{studyName(s)}</span>
                            <span style={{ fontSize: 12, color: '#8a8a8f' }}>{s.images} images</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 10, background: '#fff', border: '1px solid #ebebea' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: '#8a8a8f' }}>COLLECTION</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: group || target || (isNew && session.label.trim()) ? '#3f3f44' : '#0f0f10' }}>{col.name}</span>
            </div>
            {group ? (
              <>
                <IconChevronRight size={14} color="#c4c4c2" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: '#8a8a8f' }}>GROUP</span>
                  <span style={{ fontSize: 14, fontWeight: 500, color: target || (isNew && session.label.trim()) ? '#3f3f44' : '#0f0f10' }}>{group.name}</span>
                </div>
              </>
            ) : null}
            {(isNew && session.label.trim()) || target ? (
              <>
                <IconChevronRight size={14} color="#c4c4c2" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: '#8a8a8f' }}>{isNew ? 'NEW STUDY' : 'STUDY'}</span>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{isNew ? session.label : target ? `${studyName(target)} · ${target.images} images` : ''}</span>
                </div>
              </>
            ) : null}
          </div>
          {refusal && !['study_id', 'age', 'diagnosis', 'description'].includes(refusal.field ?? '') ? <Notice tone="danger">{refusal.message}</Notice> : null}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingTop: 4 }}>
            <Button variant="primary" size={36} disabled={!canContinue} onClick={() => void next()}>
              {saving ? 'Saving…' : 'Continue to upload'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
